import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Undo2 } from 'lucide-react';
import Alert from '../components/Alert.jsx';
import Loader from '../components/Loader.jsx';
import { api } from '../api/client.js';

// /my-requests — user-facing tracker for the design requests they've
// submitted. Mirrors the admin queue's status-filter pattern so users
// can scan active vs. fulfilled vs. denied without scrolling through
// history. Default filter is "active" (pending + in_progress) since
// those are the ones still waiting on a decision.
//
// Access-requests aren't shown here — they're surfaced inline on the
// per-package /access/<slug> matrix as "pending" rows. Could be added
// here as a separate tab once a GET endpoint exists.

const FILTERS = [
  { id: 'active',    label: 'Active',    statuses: ['pending', 'in_progress'] },
  { id: 'fulfilled', label: 'Fulfilled', statuses: ['fulfilled'] },
  { id: 'denied',    label: 'Denied',    statuses: ['denied'] },
  { id: 'all',       label: 'All',       statuses: null },
];

function StatusBadge({ status }) {
  const cls =
    status === 'pending'     ? 'bg-secondary'        :
    status === 'in_progress' ? 'bg-warning text-dark':
    status === 'fulfilled'   ? 'bg-success'          :
    'bg-danger';
  const label = status === 'in_progress' ? 'in progress' : status;
  return <span className={`badge ${cls}`}>{label}</span>;
}

export default function MyRequests() {
  const [requests, setRequests] = useState(null);
  const [error, setError] = useState(null);
  const [filterId, setFilterId] = useState('active');

  useEffect(() => {
    let alive = true;
    api.listMyDesignRequests()
      .then((data) => { if (alive) setRequests(data?.requests ?? []); })
      .catch((e) => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    if (!requests) return null;
    const f = FILTERS.find((x) => x.id === filterId);
    if (!f?.statuses) return requests;
    return requests.filter((r) => f.statuses.includes(r.status));
  }, [requests, filterId]);

  const counts = useMemo(() => {
    const out = { active: 0, fulfilled: 0, denied: 0, all: 0 };
    for (const r of requests ?? []) {
      out.all++;
      if (r.status === 'pending' || r.status === 'in_progress') out.active++;
      else if (r.status === 'fulfilled') out.fulfilled++;
      else if (r.status === 'denied') out.denied++;
    }
    return out;
  }, [requests]);

  return (
    <div className="row justify-content-center mt-4">
      <div className="col-xl-11">
        <section className="dashboard-hero">
          <div className="dashboard-hero-glow" aria-hidden="true" />
          <p className="dashboard-hero-eyebrow">Design Request Tracker</p>
          <h1 className="dashboard-hero-title">
            <span className="dashboard-hero-name">My</span> Requests
          </h1>
          <p className="dashboard-hero-sub">
            Custom overlay build requests you've submitted across all packages.
          </p>
        </section>

        <div className="dashboard-stats" role="group" aria-label="Filter by status">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={`dashboard-stat dashboard-stat-clickable${f.id === filterId ? ' is-active' : ''}`}
              onClick={() => setFilterId(f.id)}
              aria-pressed={f.id === filterId}
            >
              <div className="dashboard-stat-value">{counts[f.id] ?? 0}</div>
              <div className="dashboard-stat-label">{f.label}</div>
            </button>
          ))}
        </div>

        {error && (
          <Alert kind="danger" onClose={() => setError(null)}>{error}</Alert>
        )}

        {filtered === null ? (
          <Loader label="Loading…" />
        ) : filtered.length === 0 ? (
          <div className="card">
            <div className="card-body text-center py-5">
              <div className="text-muted mb-3">
                {filterId === 'active'
                  ? 'No active requests. You can submit one from any package\'s Access page.'
                  : `No ${filterId === 'all' ? '' : filterId + ' '}requests yet.`}
              </div>
              <Link to="/" className="btn-menu-back">
                <Undo2 size={17} aria-hidden="true" />
                Main Menu
              </Link>
            </div>
          </div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {filtered.map((r) => (
              <div className="card" key={r.id}>
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                    <div>
                      <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                        <strong>{r.requestedName}</strong>
                        <StatusBadge status={r.status} />
                      </div>
                      <div className="text-muted small">
                        {r.package?.name && (
                          <>
                            for{' '}
                            <Link to={`/access/${r.package.slug}`}>
                              {r.package.name}
                            </Link>
                            {' · '}
                          </>
                        )}
                        {r.baseOverlay && <>variant of {r.baseOverlay.name} · </>}
                        submitted {new Date(r.requestedAt).toLocaleString()}
                        {r.decidedAt && (
                          <> · decided {new Date(r.decidedAt).toLocaleString()}</>
                        )}
                      </div>
                    </div>
                    {r.status === 'fulfilled' && r.fulfilledOverlay && (
                      <Link
                        to={`/${r.package?.slug ?? ''}`}
                        className="btn btn-outline-success btn-sm"
                      >
                        Open in package →
                      </Link>
                    )}
                  </div>
                  {r.description && (
                    <div className="mt-2 small" style={{ whiteSpace: 'pre-wrap' }}>
                      {r.description}
                    </div>
                  )}
                  {r.notes && (
                    <div className="mt-2 small text-muted">
                      <strong>Admin notes:</strong> {r.notes}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
